import { Injectable } from '@angular/core';
import {
  ConfigService,
  OrderableIngredient,
  OrderedItem,
  IngredientChoice,
  ExtraIngredientUpsellStrategy,
  OrderedItemService,
  UpsellType,
} from 'ngx-web-api';
import { Observable, Subject, of } from 'rxjs';
import { mergeMap, map, filter, take, tap } from 'rxjs/operators';
import { ExtraUpsell } from 'app/domain/extra-upsell';

@Injectable({
  providedIn: 'root',
})
export class ExtraIngredientUpsellService {
  private extraUpsellStrategy: ExtraIngredientUpsellStrategy;
  private popupOpen = false;
  private fetchingPrice = false;

  private extraUpsell: Subject<ExtraUpsell> = new Subject();
  public extraUpsell$: Observable<ExtraUpsell> = this.extraUpsell.asObservable();

  private deniedExtraUpsell: Subject<boolean> = new Subject();
  public deniedExtraUpsell$: Observable<boolean> = this.deniedExtraUpsell.asObservable();

  private documentClickEvent: Subject<Event> = new Subject();
  public documentClickEvent$: Observable<Event> = this.documentClickEvent.asObservable();

  constructor(private config: ConfigService, private orderedItemService: OrderedItemService) {
    this.config.storeConfig$.pipe(map(store => store.extraIngredientUpsellStrategy)).subscribe(c => (this.extraUpsellStrategy = c));
  }

  public getQualifierUpsell(ingredient: OrderableIngredient, choice: IngredientChoice, item: OrderedItem): Observable<ExtraUpsell> {
    if (
      this.fetchingPrice ||
      this.extraUpsellStrategy === ExtraIngredientUpsellStrategy.NONE ||
      (this.extraUpsellStrategy === ExtraIngredientUpsellStrategy.MARKED_UPSELL && !ingredient.isExtraIngredientUpsell) ||
      (this.extraUpsellStrategy === ExtraIngredientUpsellStrategy.FIRST_PRICED &&
        (!ingredient.isExtraIngredientUpsell || item.upsellType === UpsellType.EXTRA_INGREDIENT))
    ) {
      return of(null);
    }

    const firstPricedQual = ingredient.qualifiers.filter(q => q.priceFactor > 0 && !q.isDefault).sort()[0];
    if (!firstPricedQual) {
      return of(null);
    }

    this.fetchingPrice = true;

    return this.orderedItemService.listenForPriceChange().pipe(
      map(startPrice => {
        const itemCopy = item.copy();
        const orderedQualifiers = itemCopy.getChoice(choice.name).ingredients.find(i => i.ingredient === ingredient.name).qualifiers;
        const newQualifiers = [firstPricedQual.name];
        if (!!orderedQualifiers) {
          newQualifiers.push(...orderedQualifiers);
        }
        itemCopy.getChoice(choice.name).ingredients.find(i => i.ingredient === ingredient.name).qualifiers = newQualifiers;
        return { item: itemCopy, price: startPrice, qualifier: firstPricedQual };
      }),
      mergeMap(itemPriceObject =>
        this.orderedItemService.getPrice(itemPriceObject.item).pipe(
          map(newPrice => ({ startPrice: itemPriceObject.price, newPrice: newPrice, qualifier: itemPriceObject.qualifier })),
          tap(() => (this.fetchingPrice = false))
        )
      ),
      filter(extraQual => extraQual.startPrice !== extraQual.newPrice),
      map(extraQual => {
        const diff = parseFloat((extraQual.newPrice - extraQual.startPrice).toFixed(2));
        return {
          choice: choice.name,
          ingredient: ingredient.name,
          priceDiff: diff,
          qualifier: extraQual.qualifier,
        };
      }),
      take(1)
    );
  }

  public setQualifierUpsell(qualifierUpsell: ExtraUpsell) {
    if (!this.popupOpen) {
      this.extraUpsell.next(qualifierUpsell);
    }
  }

  public addExtraUpsellToOrdered(item: OrderedItem, extraUpsell: ExtraUpsell) {
    const upsold = {
      ingredient: extraUpsell.ingredient,
      itemSize: item.size,
      qualifierName: extraUpsell.qualifier.name,
      priceDiff: Math.round(extraUpsell.priceDiff * 100),
    };
    if (item.extraIngredientUpsells) {
      if (
        !item.extraIngredientUpsells.some(
          extra => extra.ingredient === extraUpsell.ingredient && extra.qualifierName === extraUpsell.qualifier.name
        )
      ) {
        item.extraIngredientUpsells.push(upsold);
      }
    } else {
      item.extraIngredientUpsells = [upsold];
    }
  }

  public removeExtraUpsellFromOrdered(item: OrderedItem, ingredientName: string) {
    if (!item.extraIngredientUpsells) {
      return;
    }
    const removeIndex = item.extraIngredientUpsells.map(upsell => upsell.ingredient).indexOf(ingredientName);
    if (removeIndex !== -1) {
      item.extraIngredientUpsells.splice(removeIndex, 1);
    }
  }

  public denyExtraUpsell() {
    this.setPopupOpen(false);
    if (this.extraUpsellStrategy === ExtraIngredientUpsellStrategy.FIRST_PRICED) {
      this.deniedExtraUpsell.next(true);
    }
  }

  public setPopupOpen(open: boolean) {
    this.popupOpen = open;
  }

  public isPopupOpen() {
    return this.popupOpen;
  }

  public setClickEvent(event: any) {
    this.documentClickEvent.next(event);
  }
}
